import { stableSerialize } from '@erp/shared-utils';

interface PendingLaunch {
  requestId: string;
  active: number;
  failed: boolean;
}

/** Keep each semantic launch retryable until all concurrent calls succeed.
 * A mixed success/failure batch retains its id because one caller still has an
 * uncertain outcome. A later successful retry releases it for a new launch. */
export function createLaunchRequestTracker(
  createId: () => string = () => crypto.randomUUID(),
) {
  const pending = new Map<string, PendingLaunch>();
  return async function launch<Result>(
    payload: unknown,
    dispatch: (requestId: string) => Promise<Result>,
  ): Promise<Result> {
    const fingerprint = stableSerialize(payload);
    let identity = pending.get(fingerprint);
    if (!identity) {
      identity = { requestId: createId(), active: 0, failed: false };
      pending.set(fingerprint, identity);
    }
    if (identity.active === 0) identity.failed = false;
    identity.active += 1;

    try {
      return await dispatch(identity.requestId);
    } catch (error) {
      identity.failed = true;
      throw error;
    } finally {
      identity.active -= 1;
      if (identity.active === 0 && !identity.failed)
        pending.delete(fingerprint);
    }
  };
}
